/**
 * @fileoverview Slack Integration Configuration Component
 * Allows users to connect Slack, configure notification channels,
 * and manage their Slack integration settings.
 */

import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MessageSquare,
  Check,
  Loader2,
  AlertCircle,
  ExternalLink,
  Hash,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

// Slack OAuth URL configuration
const SLACK_CLIENT_ID = import.meta.env.VITE_SLACK_CLIENT_ID || '';
const SLACK_REDIRECT_URI = `${window.location.origin}/settings/integrations/slack/callback`;
const SLACK_SCOPES = [
  'chat:write',
  'channels:read',
  'groups:read',
  'channels:join',
].join(',');

interface SlackChannel {
  id: string;
  name: string;
  isPrivate: boolean;
}

interface SlackIntegration {
  id: string;
  status: 'active' | 'disconnected' | 'error';
  config: {
    team_id?: string;
    team_name?: string;
    bot_user_id?: string;
    channels?: {
      new_call?: string;
      missed_call?: string;
      completed_call?: string;
    };
  };
  last_error?: string;
  created_at: string;
}

export function SlackConfig() {
  const { activeTenantId } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [selectedChannels, setSelectedChannels] = useState<{
    new_call: string;
    missed_call: string;
    completed_call: string;
  }>({
    new_call: '',
    missed_call: '',
    completed_call: '',
  });

  // Fetch current Slack integration status
  const { data: integration, isLoading: isLoadingIntegration } = useQuery({
    queryKey: ['slack-integration', activeTenantId],
    queryFn: async () => {
      if (!activeTenantId) return null;

      const { data, error } = await supabase
        .from('calie_integrations')
        .select('*')
        .eq('tenant_id', activeTenantId)
        .eq('provider', 'slack')
        .maybeSingle();

      if (error) throw error;
      return data as SlackIntegration | null;
    },
    enabled: !!activeTenantId,
  });

  // Fetch available Slack channels
  const { data: channels, isLoading: isLoadingChannels, refetch: refetchChannels } = useQuery({
    queryKey: ['slack-channels', activeTenantId],
    queryFn: async () => {
      if (!activeTenantId || integration?.status !== 'active') return [];

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('Calie_slack/channels', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { tenantId: activeTenantId },
      });

      if (response.error) throw response.error;
      return (response.data?.channels || []) as SlackChannel[];
    },
    enabled: !!activeTenantId && integration?.status === 'active',
  });

  // Initialize selected channels from integration config
  useEffect(() => {
    if (integration?.config?.channels) {
      setSelectedChannels({
        new_call: integration.config.channels.new_call || '',
        missed_call: integration.config.channels.missed_call || '',
        completed_call: integration.config.channels.completed_call || '',
      });
    }
  }, [integration]);

  // Save channel configuration
  const saveChannelsMutation = useMutation({
    mutationFn: async () => {
      if (!activeTenantId || !integration) throw new Error('No integration');

      const { error } = await supabase
        .from('calie_integrations')
        .update({
          config: {
            ...integration.config,
            channels: selectedChannels,
          },
          updated_at: new Date().toISOString(),
        })
        .eq('id', integration.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slack-integration', activeTenantId] });
      toast({
        title: 'Channels updated',
        description: 'Your Slack notification channels have been saved.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Error saving channels',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Disconnect Slack
  const disconnectMutation = useMutation({
    mutationFn: async () => {
      if (!activeTenantId || !integration) throw new Error('No integration');

      const { error } = await supabase
        .from('calie_integrations')
        .delete()
        .eq('id', integration.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slack-integration', activeTenantId] });
      toast({
        title: 'Slack disconnected',
        description: 'Your Slack integration has been removed.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Error disconnecting',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Test notification
  const testNotificationMutation = useMutation({
    mutationFn: async () => {
      if (!activeTenantId || !selectedChannels.new_call) {
        throw new Error('Select a channel first');
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('Calie_slack/test', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: {
          tenantId: activeTenantId,
          channelId: selectedChannels.new_call,
        },
      });

      if (response.error) throw response.error;
      if (!response.data?.success) throw new Error(response.data?.error || 'Test failed');
    },
    onSuccess: () => {
      toast({
        title: 'Test message sent',
        description: 'Check your Slack channel for the test notification.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Test failed',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Handle Slack OAuth
  const handleConnect = () => {
    const state = btoa(JSON.stringify({ tenantId: activeTenantId }));
    const authUrl = `https://slack.com/oauth/v2/authorize?client_id=${SLACK_CLIENT_ID}&scope=${SLACK_SCOPES}&redirect_uri=${encodeURIComponent(SLACK_REDIRECT_URI)}&state=${state}`;
    window.location.href = authUrl;
  };

  if (isLoadingIntegration) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <MessageSquare className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <CardTitle>Slack</CardTitle>
              <CardDescription>
                Receive call notifications in your Slack workspace
              </CardDescription>
            </div>
          </div>
          {integration?.status === 'active' && (
            <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
              <Check className="h-3 w-3 mr-1" />
              Connected
            </Badge>
          )}
          {integration?.status === 'error' && (
            <Badge variant="destructive">
              <AlertCircle className="h-3 w-3 mr-1" />
              Error
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Error Alert */}
        {integration?.status === 'error' && integration.last_error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {integration.last_error}. Please reconnect your Slack workspace.
            </AlertDescription>
          </Alert>
        )}

        {/* Not Connected State */}
        {!integration && (
          <div className="text-center py-6">
            <p className="text-muted-foreground mb-4">
              Connect your Slack workspace to receive real-time call notifications.
            </p>
            <Button onClick={handleConnect} className="gap-2">
              <ExternalLink className="h-4 w-4" />
              Connect Slack
            </Button>
          </div>
        )}

        {/* Connected State */}
        {integration?.status === 'active' && (
          <>
            {/* Workspace Info */}
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Connected Workspace</p>
                  <p className="font-medium">{integration.config.team_name || 'Unknown'}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => refetchChannels()}
                  disabled={isLoadingChannels}
                >
                  <RefreshCw className={`h-4 w-4 ${isLoadingChannels ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>

            {/* Channel Configuration */}
            <div className="space-y-4">
              <h4 className="font-medium">Notification Channels</h4>
              <p className="text-sm text-muted-foreground">
                Select which channels to send notifications to for different call events.
              </p>

              {/* New Call Channel */}
              <div className="space-y-2">
                <Label htmlFor="new-call-channel">New Calls</Label>
                <Select
                  value={selectedChannels.new_call}
                  onValueChange={(v) => setSelectedChannels(prev => ({ ...prev, new_call: v }))}
                >
                  <SelectTrigger id="new-call-channel">
                    <SelectValue placeholder="Select a channel" />
                  </SelectTrigger>
                  <SelectContent>
                    {channels?.map(channel => (
                      <SelectItem key={channel.id} value={channel.id}>
                        <div className="flex items-center gap-2">
                          <Hash className="h-3 w-3" />
                          {channel.name}
                          {channel.isPrivate && <Badge variant="outline" className="text-xs">Private</Badge>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Missed Call Channel */}
              <div className="space-y-2">
                <Label htmlFor="missed-call-channel">Missed Calls</Label>
                <Select
                  value={selectedChannels.missed_call}
                  onValueChange={(v) => setSelectedChannels(prev => ({ ...prev, missed_call: v }))}
                >
                  <SelectTrigger id="missed-call-channel">
                    <SelectValue placeholder="Select a channel (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Same as New Calls</SelectItem>
                    {channels?.map(channel => (
                      <SelectItem key={channel.id} value={channel.id}>
                        <div className="flex items-center gap-2">
                          <Hash className="h-3 w-3" />
                          {channel.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Completed Call Channel */}
              <div className="space-y-2">
                <Label htmlFor="completed-call-channel">Completed Calls</Label>
                <Select
                  value={selectedChannels.completed_call}
                  onValueChange={(v) => setSelectedChannels(prev => ({ ...prev, completed_call: v }))}
                >
                  <SelectTrigger id="completed-call-channel">
                    <SelectValue placeholder="Select a channel (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Same as New Calls</SelectItem>
                    {channels?.map(channel => (
                      <SelectItem key={channel.id} value={channel.id}>
                        <div className="flex items-center gap-2">
                          <Hash className="h-3 w-3" />
                          {channel.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 pt-4 border-t">
              <Button
                onClick={() => saveChannelsMutation.mutate()}
                disabled={saveChannelsMutation.isPending}
              >
                {saveChannelsMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Save Channels
              </Button>

              <Button
                variant="outline"
                onClick={() => testNotificationMutation.mutate()}
                disabled={testNotificationMutation.isPending || !selectedChannels.new_call}
              >
                {testNotificationMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <MessageSquare className="h-4 w-4 mr-2" />
                )}
                Send Test
              </Button>

              <Button
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => disconnectMutation.mutate()}
                disabled={disconnectMutation.isPending}
              >
                {disconnectMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Disconnect
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
